import { Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useMediaQuery } from '@mui/material';
import React, { useContext, useState } from 'react'
import globalStyles from "../../../assets/styles/global.module.scss";
import styles from "../../../assets/styles/componentStyles/intentStyles/intentStyle.module.scss";
import dashboardStyles from './../../../assets/styles/componentStyles/callAnalysisStyles/callAnalysisDashboard.module.scss';
import { useIntentStore } from '../../../store/intentStore';
import GlobalPopUp from '../../utility/globalPopUp';
import CreateIntentPopup from './createIntentPopup';
import { globalMenuItemData, intent } from '../../../models/axiosModel';
import GlobalMenu from '../../globalMenu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import globalStrenameDocumentyles from "../../../assets/renameDocument.svg";
import { formatDateAccordingToTimezoneUsingMoment, hasIntentManageAccess, toCapitalizedCase } from '../../../utils/constants';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import addSubMenuIcon from "../../../assets/addSubMenuIcon.png"; 
import deleteDoc from "../../../assets/deleteDoc.svg";
import GlobalDeletePopup from '../../utility/globalDeletePopup/globalDeletePopup';
import agent from '../../../services/api';
import { useGenericStore } from '../../../store/genericStore';
import { useShallow } from 'zustand/react/shallow';

interface IntentTableProps {
    getAnalyticsIntentApi: () => Promise<void>,
} 

const IntentTable: React.FC<IntentTableProps> = (props) => {

    const { getAnalyticsIntentApi } = props;

    const { filteredIntentDataList, intentDataList, flagCreateIntentPopup, setFlagCreateIntentPopup } = useIntentStore();

    const { userDetail, handleError, setFlagLoading, apiRoutesPermissionAssignment } = useContext(UserContext) as UserContextPayload

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    const innerHeight = window.innerHeight - 200;

    const breakPoints = {
        smallScreen: useMediaQuery<boolean>('(max-width : 600px) and (min-width : 0px)'),
        flagTabletScreen: useMediaQuery<boolean>('(max-width : 900px) and (min-width : 600px)'),
        flagSmallScreen: useMediaQuery<boolean>('(min-width: 0px) and (max-width: 1000px)'),
        flagLaptopScreen: useMediaQuery<boolean>('(min-width: 0px) and (max-width: 1550px)')
    }

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement | SVGSVGElement>(null);
    const [currentRowIndex, setCurrentRowIndex] = useState<number | string | null>(null);

    const [currentRowIndexForAdd, setCurrentRowIndexForAdd] = useState<number | string | null>(null);
    const [selectedIntent, setSelectedIntent] = useState<intent | null>(null); 
    const [flagSubIntentPopup, setFlagSubIntentPopup] = useState<boolean>(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);    

    const [hoveredRow, setHoveredRow] = useState<intent | null>(null);

    const getIntentActionMenu = (intent: intent): globalMenuItemData[] => [
        {
            type: "item",
            label: "Edit", 
            icon: <img alt="" src={globalStrenameDocumentyles} />, 
            isDisable: false,
            onClick: (intent: intent) => {
                setSelectedIntent(intent);
                setFlagSubIntentPopup(false);
                setFlagCreateIntentPopup(true);
            }
        }, 
        ...(hasIntentManageAccess(apiRoutesPermissionAssignment, userDetail) ? [
            {
                type: "item",
                label: "Delete",
                icon: <img alt="" src={deleteDoc} />, 
                isDisable: intent?.child_intent?.length !== 0,
                onClick: (intent: intent) => {
                    setSelectedIntent(intent);
                    setShowDeleteConfirmation(true);
                }
            } as globalMenuItemData
        ] : [])
    ]    

    const findIntentById = (id: string, intents: intent[]): intent | null => {
        for (const item of intents) {
            if (item.intent_id === id) return item;
            if (item.child_intent?.length) {
                const found = findIntentById(id, item.child_intent);
                if (found) return found;
            }
        }
        return null;
    };

    const getIntentDepth = (intent: intent, intentList: intent[]): number => {
        let depth = 1;
        let current = intent;

        while (current?.parent_intent_id) {
            const parent = findIntentById(current.parent_intent_id, intentList);
            if (!parent) break;

            depth++;
            current = parent;
        }

        return depth;
    };

    const getMenuItemForAddIcon = (intent: intent): globalMenuItemData[] => [
        {
            type: "item",
            label: "Add New Intent", 
            onClick: (intent: intent) => {
                const parentIntentOfSelectedIntent = findIntentById(intent?.parent_intent_id ?? "" , intentDataList ?? []);
                if(parentIntentOfSelectedIntent){
                    setSelectedIntent(parentIntentOfSelectedIntent);     
                    setFlagSubIntentPopup(true);
                } else {
                    setSelectedIntent(null);     
                    setFlagSubIntentPopup(false);
                }

                setFlagCreateIntentPopup(true); 
            }, 
            isDisable: false,
        },
        {
            type: "item",
            label: "Add Sub Intent", 
            onClick: (intent: intent) => {
                setSelectedIntent(intent); 

                setFlagSubIntentPopup(true); 
                setFlagCreateIntentPopup(true); 
            }, 
            isDisable: (() => {
                if (!intentDataList) return true; 
                if(intent?.flag_remaining) return true;
                const depth = getIntentDepth(intent, intentDataList);
                return depth >= 3;
            })(),
        },
    ]

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement | SVGSVGElement, MouseEvent>, rowIndex: number | string) => {
        setCurrentRowIndex(rowIndex);
        setAnchorEl(event.currentTarget);
    };
    
    const handleMenuClose = () => {
        setCurrentRowIndex(null);
        setAnchorEl(null);
    };

    const handleMenuOpenForAddIcon = (event: React.MouseEvent<HTMLElement | SVGSVGElement, MouseEvent>, rowIndex: number | string) => {
        setCurrentRowIndexForAdd(rowIndex);
        setAnchorEl(event.currentTarget);
    } 

    const handleMenuCloseForAddIcon = () => {
        setCurrentRowIndexForAdd(null);
        setAnchorEl(null);
    } 

    const sortIntents = (intent: intent[]) => {
        return intent.sort((a,b) => {
            return new Date(a.created_at_timestamp).getTime() - new Date(b.created_at_timestamp).getTime();
        })
    }

    const renderIntentItems = (intent: intent[], parentPrefix: string = ""): JSX.Element[] => { 
        const sortedIntents = sortIntents(intent);
        return sortedIntents.flatMap((intent, index) => {
            const prefix = parentPrefix ? `${parentPrefix}.${index + 1}` : `${index + 1}`;

            const row = (
                <TableRow className={dashboardStyles.cellRow} key={intent.intent_id} onMouseEnter={() => setHoveredRow(intent)} onMouseLeave={() => setHoveredRow(null)}>
                    <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle}>
                        <Grid item sx={{ display: "flex", width: "1px" }}>
                            {(hoveredRow?.intent_id == intent.intent_id) || (currentRowIndexForAdd == intent?.intent_id) ?
                                <>
                                    <img 
                                        src={addSubMenuIcon} 
                                        className={globalStyles.clickable}
                                        onClick={(e) => { e.stopPropagation(); handleMenuOpenForAddIcon(e, intent.intent_id) }} 
                                    />
                                    <GlobalMenu
                                        anchorEl={anchorEl}
                                        handleMenuClose={handleMenuCloseForAddIcon}
                                        menuItemData={getMenuItemForAddIcon(intent)}
                                        open={Boolean(anchorEl) && currentRowIndexForAdd == intent?.intent_id}
                                        additionalField={intent}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    />
                                </>
                                : 
                                <></>
                            }
                        </Grid>
                    </TableCell>
                    <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle}>{prefix}</TableCell>
                    <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle}>{intent?.intent_name}</TableCell>
                    <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle}>
                        <Grid container gap={1} alignItems={"center"} flexWrap={"nowrap"}>
                            <Grid item className={styles.intentColorGridBox} sx={{backgroundColor: `${intent?.intent_color} !important`}}></Grid>
                            <Grid item>
                                {intent?.intent_color}
                            </Grid>
                        </Grid>
                    
                    </TableCell> 
                    <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle}>{toCapitalizedCase(String(intent?.flag_remaining))}</TableCell>
                    <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle}>{toCapitalizedCase(String(intent?.flag_show))}</TableCell>
                    <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle}>
                        {formatDateAccordingToTimezoneUsingMoment(intent?.updated_at_timestamp ?? intent?.created_at_timestamp , userDetail.timezone_hour)}
                    </TableCell>
                    <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${dashboardStyles.viewButtonColumnStyle}`} sx={{textAlign: "end"}}>
                        <MoreVertIcon
                            className={globalStyles.clickable}
                            onClick={(e: any) => { e.stopPropagation(); handleMenuOpen(e, intent.intent_id) }}
                        />
                        <GlobalMenu
                            anchorEl={anchorEl}
                            handleMenuClose={handleMenuClose}
                            menuItemData={getIntentActionMenu(intent)}
                            open={Boolean(anchorEl) && intent.intent_id === currentRowIndex}
                            additionalField={intent}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        />
                    </TableCell>
                </TableRow>
            );

            const children = intent.child_intent && intent.child_intent?.length > 0 ? renderIntentItems(intent.child_intent, prefix) : [];

            return [row, ...children];
        })
    }

    const deleteIntent = async (selectedIntent: intent | null) => {
        if(selectedIntent){
            setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
            const res = await agent.deleteAnalyticsIntent.delete(selectedIntent?.intent_id);
            if(!(res.status)) {
                handleError(res);
            } else {
                showSuccess("Intent Deleted Successfully.")
                getAnalyticsIntentApi();
            }
            setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
        }
    }

    return (
        <>
            <Grid item className={globalStyles.fullwidth}>
                <TableContainer className={`${styles.intentTableContainer} ${globalStyles.overflowAuto}`} sx={{ maxHeight: `${innerHeight}px !important` }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}></TableCell>
                                <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>#</TableCell>
                                <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Intent Name</TableCell>
                                <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Intent Color</TableCell>
                                <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Flag Remaining</TableCell>
                                <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Display in Dashboard Insights</TableCell>
                                <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Updated At</TableCell>
                                <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${dashboardStyles.viewButtonColumnHeadingStyle}`} sx={{textAlign: "end"}}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredIntentDataList && filteredIntentDataList?.length > 0 ? 
                                renderIntentItems(filteredIntentDataList)
                                :
                                <TableRow className={dashboardStyles.cellRow}>
                                    <TableCell colSpan={12} className={globalStyles.textCenter}>
                                        No Data Available
                                    </TableCell>
                                </TableRow>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>

            {flagCreateIntentPopup && 
                <GlobalPopUp
                    open={flagCreateIntentPopup}
                    onClose={() => { setFlagCreateIntentPopup(false); setSelectedIntent(null); }}
                    handleClose={() => { setFlagCreateIntentPopup(false); setSelectedIntent(null);}}
                    flagShowCloseIcon={true}
                    paperMinWidth={breakPoints.flagSmallScreen ? "300px" : "600px"}
                    paperMaxwidth={breakPoints.flagSmallScreen ? "710px" : "700px"}
                    borderRadius='10px'
                    titlePadding='0px'
                    overflow="hidden"
                >
                    <CreateIntentPopup
                        selectedIntent={selectedIntent}  
                        setSelectedIntent={setSelectedIntent}
                        flagSubIntentPopup={flagSubIntentPopup}
                        findIntentById={findIntentById}
                    />
                </GlobalPopUp>
            } 

            {showDeleteConfirmation &&
                <GlobalDeletePopup
                    deleteDescription={`This intent might be used in rule-based chatbot configurations. If you delete this intent, the intent configurations in associated rule-based chatbot will also be removed.\n\n Are you sure you want to delete ${selectedIntent?.intent_name} intent`}
                    open={showDeleteConfirmation}
                    onClose={() => { setShowDeleteConfirmation(false); setSelectedIntent(null); }}
                    onDelete={() => {
                        deleteIntent(selectedIntent);
                        setShowDeleteConfirmation(false);
                        setSelectedIntent(null);
                    }}
                />
            }
        </>
    )
}

export default IntentTable
